import { Button, If } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	Form,
	RadioField,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import {
	EmployeeFormValues,
	PersonFormValues,
	TeacherCourseSectionEnrollmentFormValues,
	TeacherHomeRoomSectionEnrollmentFormValues,
} from "../../FormUtilities";
import { CampusSelectField } from "../Campus";
import { ClassYearSelectField } from "../ClassYear";
import { ClassYearCategorySelectField } from "../ClassYearCategory";
import { PersonForm, PersonSelectField } from "../Person";
import { BaseFieldProps } from "../shared";
import { TeacherCourseSectionEnrollmentForm } from "../TeacherCourseSectionEnrollment";
import { TeacherHomeRoomSectionEnrollmentForm } from "../TeacherHomeRoomSectionEnrollment";

/**
 * Generic props for fields of the Employee model.
 */
type FieldProps<K extends keyof EmployeeFormValues.Base = keyof EmployeeFormValues.Base> = BaseFieldProps<
	Pick<EmployeeFormValues.Base, K>
>;

/**
 * Generic props for fields of the Employee model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof EmployeeFormValues.Create = keyof EmployeeFormValues.Create> = BaseFieldProps<
	Pick<EmployeeFormValues.Create, K>
>;

/**
 * Generic props for fields of the Employee model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof EmployeeFormValues.Detail = keyof EmployeeFormValues.Detail> = BaseFieldProps<
	Pick<EmployeeFormValues.Detail, K>
>;

/**
 * Field component for the `workEmailAddress` field of the Employee model.
 */
export function WorkEmailAddress({ formState, ...props }: FieldProps<"workEmailAddress">) {
	return <TextField formState={formState} name="workEmailAddress" {...props} />;
}

/**
 * Field component for the `jobTitle` field of the Employee model.
 */
export function JobTitle({ formState, ...props }: FieldProps<"jobTitle">) {
	return <TextField formState={formState} name="jobTitle" {...props} />;
}

/**
 * Field component for the `role` field of the Employee model.
 */
export function Role({ formState, ...props }: FieldProps<"role">) {
	return (
		<RadioField
			formState={formState}
			name="role"
			options={EmployeeFormValues.roleOptions}
			blankValue={null}
			{...props}
		/>
	);
}

/**
 * Field component for the `startDate` field of the Employee model.
 */
export function StartDate({ formState, ...props }: FieldProps<"startDate">) {
	return <DateTimeField formState={formState} name="startDate" precision="day" {...props} />;
}

/**
 * Field component for the `endDate` field of the Employee model.
 */
export function EndDate({ formState, ...props }: FieldProps<"endDate">) {
	return <DateTimeField formState={formState} name="endDate" optional precision="day" {...props} />;
}

/**
 * Field component for the `bio` field of the Employee model.
 */
export function Bio({ formState, ...props }: FieldProps<"bio">) {
	return <TextField formState={formState} name="bio" optional Input={BioInput} {...props} />;
}

function BioInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={3} {...props} />;
}

export namespace Campuses {
	export type SelectProps = FieldProps<"campusIDs"> &
		Omit<CampusSelectField.MultiSelectProps<EmployeeFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `campuses` edge of the Employee model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CampusSelectField.MultiSelect formState={formState} name="campusIDs" {...props} />;
	}
}

export namespace ClassYearCategories {
	const defaultHint =
		"Connect this staff member to a collection of class years. Not necessary for teachers, useful for principals and deans.";

	export type SelectProps = FieldProps<"classYearCategoryIDs"> &
		Omit<ClassYearCategorySelectField.MultiSelectProps<EmployeeFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `classYearCategories` edge of the Employee model.
	 */
	export function Select({ formState, hint = defaultHint, ...props }: SelectProps) {
		return (
			<ClassYearCategorySelectField.MultiSelect
				formState={formState}
				name="classYearCategoryIDs"
				hint={hint}
				{...props}
			/>
		);
	}
}

export namespace ClassYears {
	const defaultHint =
		"Connect this staff member to a class year. Not necessary for teachers, useful for principals and deans.";

	export type SelectProps = FieldProps<"classYearIDs"> &
		Omit<ClassYearSelectField.MultiSelectProps<EmployeeFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `classYears` edge of the Employee model.
	 */
	export function Select({ formState, hint = defaultHint, ...props }: SelectProps) {
		return <ClassYearSelectField.MultiSelect formState={formState} name="classYearIDs" hint={hint} {...props} />;
	}
}

export namespace Person {
	export type SelectProps = DetailFieldProps<"personID"> &
		Omit<PersonSelectField.SelectProps<EmployeeFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `person` edge of the Employee model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PersonSelectField.Select formState={formState} name="personID" {...props} />;
	}

	/**
	 * SubForm field component for the `person` edge of the Employee model.
	 */
	export function SubForm({ formState, noHeader = true, style, ...props }: CreateFieldProps<"person">) {
		return (
			<SubFormField
				formState={formState}
				name="person"
				blankItem={PersonFormValues.blankItemInEmployee}
				minItems={1}
				maxItems={1}
				noHeader={noHeader}
				style={{ "--sub-form-field__value---padding": 0, ...style } as React.CSSProperties}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<PersonFormValues.CreateInEmployee>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Person
					</Button>
				</If>
				<PersonForm.CreateFieldsInEmployee formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<Pick<EmployeeFormValues.Create, "person">, PersonFormValues.CreateInEmployee>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Person
				</Button>
			</If>
		);
	};
}

export namespace TeacherCourseSectionEnrollments {
	/**
	 * SubForm field component for the `teacherCourseSectionEnrollments` edge of the Employee model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"teacherCourseSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="teacherCourseSectionEnrollments"
				blankItem={TeacherCourseSectionEnrollmentFormValues.blankItemInTeacher}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<TeacherCourseSectionEnrollmentFormValues.CreateInTeacher>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Teacher Course Section Enrollment
				</Button>
				<TeacherCourseSectionEnrollmentForm.CreateFieldsInTeacher formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<EmployeeFormValues.Create, "teacherCourseSectionEnrollments">,
		TeacherCourseSectionEnrollmentFormValues.CreateInTeacher
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Teacher Course Section Enrollment
			</Button>
		);
	};
}

export namespace TeacherHomeRoomSectionEnrollments {
	/**
	 * SubForm field component for the `teacherHomeRoomSectionEnrollments` edge of the Employee model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"teacherHomeRoomSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="teacherHomeRoomSectionEnrollments"
				blankItem={TeacherHomeRoomSectionEnrollmentFormValues.blankItemInTeacher}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<TeacherHomeRoomSectionEnrollmentFormValues.CreateInTeacher>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Teacher Home Room Section Enrollment
				</Button>
				<TeacherHomeRoomSectionEnrollmentForm.CreateFieldsInTeacher formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<EmployeeFormValues.Create, "teacherHomeRoomSectionEnrollments">,
		TeacherHomeRoomSectionEnrollmentFormValues.CreateInTeacher
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Teacher Home Room Section Enrollment
			</Button>
		);
	};
}
